import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Heart,
  Sparkles,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Flame,
  Trophy,
  Target
} from "lucide-react";
import { format } from "date-fns";

export default function CheckInHistory({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkins, setCheckins] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCheckin, setExpandedCheckin] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-expand a check-in when arriving via a "View Check-In" link (?checkin_id=...) from a chat message
  useEffect(() => {
    const cid = searchParams.get('checkin_id');
    if (cid) setExpandedCheckin(cid);
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [checkinsRes, streakRes] = await Promise.all([
        axios.get(`${API}/checkins?limit=52`),
        axios.get(`${API}/checkins/streak`)
      ]);
      setCheckins(checkinsRes.data);
      setStreakData(streakRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts (reverse to show oldest first for timeline)
  const chartData = [...checkins].reverse().map((checkin, index) => {
    const lovedGod = checkin.loved_well?.loved_god || 0;
    const lovedPeople = checkin.loved_well?.loved_people || 0;
    
    const fruits = checkin.fruits_of_spirit || {};
    const fruitsAvg = Object.values(fruits).length > 0 
      ? Object.values(fruits).reduce((a, b) => a + b, 0) / Object.values(fruits).length 
      : 0;
    
    const glorifying = checkin.glorifying_god || {};
    const glorifyingAvg = Object.values(glorifying).length > 0
      ? Object.values(glorifying).reduce((a, b) => a + b, 0) / Object.values(glorifying).length
      : 0;

    return {
      week: format(new Date(checkin.week_start), 'MMM d'),
      weekFull: format(new Date(checkin.week_start), 'MMMM d, yyyy'),
      lovedGod,
      lovedPeople,
      fruitsAvg: parseFloat(fruitsAvg.toFixed(1)),
      glorifyingAvg: parseFloat(glorifyingAvg.toFixed(1)),
      overall: parseFloat(((lovedGod + lovedPeople + fruitsAvg + glorifyingAvg) / 4).toFixed(1)),
      // Individual fruits
      love: fruits.love || 0,
      joy: fruits.joy || 0,
      peace: fruits.peace || 0,
      patience: fruits.patience || 0,
      kindness: fruits.kindness || 0,
      goodness: fruits.goodness || 0,
      faithfulness: fruits.faithfulness || 0,
      gentleness: fruits.gentleness || 0,
      selfControl: fruits.self_control || 0,
      // Individual glorifying items
      useOfWords: glorifying.use_of_words || 0,
      thoughtLife: glorifying.thought_life || 0,
      useOfTime: glorifying.use_of_time || 0,
      useOfMoney: glorifying.use_of_money || 0,
      managementOfTemptations: glorifying.management_of_temptations || 0
    };
  });

  // Calculate trends
  const calculateTrend = (current, previous) => {
    if (!previous) return 'neutral';
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'neutral';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  // Get color scheme based on score
  const getScoreColors = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 8) {
      return {
        border: 'border-green-200',
        bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
        icon: 'text-green-500',
        label: 'text-green-600',
        value: 'text-green-700',
        sub: 'text-green-500'
      };
    } else if (numScore >= 6) {
      return {
        border: 'border-blue-200',
        bg: 'bg-gradient-to-br from-blue-50 to-indigo-50',
        icon: 'text-blue-500',
        label: 'text-blue-600',
        value: 'text-blue-700',
        sub: 'text-blue-500'
      };
    } else if (numScore >= 4) {
      return {
        border: 'border-amber-200',
        bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
        icon: 'text-amber-500',
        label: 'text-amber-600',
        value: 'text-amber-700',
        sub: 'text-amber-500'
      };
    } else {
      return {
        border: 'border-red-200',
        bg: 'bg-gradient-to-br from-red-50 to-rose-50',
        icon: 'text-red-500',
        label: 'text-red-600',
        value: 'text-red-700',
        sub: 'text-red-500'
      };
    }
  };

  // Calculate averages for summary cards
  const avgLovedGod = chartData.length > 0 
    ? (chartData.reduce((a, b) => a + b.lovedGod, 0) / chartData.length).toFixed(1) 
    : 0;
  const avgLovedPeople = chartData.length > 0 
    ? (chartData.reduce((a, b) => a + b.lovedPeople, 0) / chartData.length).toFixed(1) 
    : 0;
  const avgFruits = chartData.length > 0 
    ? (chartData.reduce((a, b) => a + b.fruitsAvg, 0) / chartData.length).toFixed(1) 
    : 0;
  const avgGlorifying = chartData.length > 0 
    ? (chartData.reduce((a, b) => a + b.glorifyingAvg, 0) / chartData.length).toFixed(1) 
    : 0;

  // Get colors for each card based on score
  const lovedGodColors = getScoreColors(avgLovedGod);
  const lovedPeopleColors = getScoreColors(avgLovedPeople);
  const fruitsColors = getScoreColors(avgFruits);
  const glorifyingColors = getScoreColors(avgGlorifying);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
                Check-In History
              </h1>
              <p className="text-slate-600">
                Track your spiritual growth journey over time
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-slate-600">
            {checkins.length} Check-ins
          </Badge>
        </div>

        {checkins.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No check-ins yet</p>
              <Button 
                onClick={() => navigate('/checkin')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Complete Your First Check-In
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Streak Card */}
            {streakData && (
              <Card className="border-orange-200 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    {/* Current Streak */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-lg">
                        <Flame className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-orange-600 font-medium">Current Streak</p>
                        <p className="text-4xl font-bold text-orange-700">{streakData.current_streak}</p>
                        <p className="text-sm text-orange-500">weeks</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-6">
                      <div className="text-center">
                        <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-slate-700">{streakData.longest_streak}</p>
                        <p className="text-xs text-slate-500">Best Streak</p>
                      </div>
                      <div className="text-center">
                        <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-slate-700">{streakData.total_checkins}</p>
                        <p className="text-xs text-slate-500">Total Check-ins</p>
                      </div>
                    </div>

                    {/* Next Milestone */}
                    {streakData.next_milestone_info && (
                      <div className="bg-white/60 rounded-xl p-4 flex items-center gap-3">
                        <Target className="w-8 h-8 text-amber-500" />
                        <div>
                          <p className="text-xs text-slate-500">Next Milestone</p>
                          <p className="font-semibold text-slate-700">
                            {streakData.next_milestone_info.icon} {streakData.next_milestone_info.name}
                          </p>
                          <p className="text-xs text-amber-600">
                            {streakData.weeks_to_next} week{streakData.weeks_to_next !== 1 ? 's' : ''} to go!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Earned Badges */}
                  {streakData.streak_milestones && streakData.streak_milestones.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <p className="text-sm text-orange-600 font-medium mb-2">Earned Badges</p>
                      <div className="flex flex-wrap gap-2">
                        {streakData.streak_milestones.map((badge, idx) => (
                          <div 
                            key={idx}
                            className="bg-white rounded-full px-3 py-1 flex items-center gap-2 shadow-sm"
                            title={badge.description}
                          >
                            <span className="text-lg">{badge.icon}</span>
                            <span className="text-sm font-medium text-slate-700">{badge.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className={`${lovedGodColors.border} ${lovedGodColors.bg}`}>
                <CardContent className="p-4 text-center">
                  <Heart className={`w-6 h-6 ${lovedGodColors.icon} mx-auto mb-2`} />
                  <p className={`text-xs ${lovedGodColors.label} font-medium`}>Avg Loved God</p>
                  <p className={`text-2xl font-bold ${lovedGodColors.value}`}>{avgLovedGod}</p>
                  <p className={`text-xs ${lovedGodColors.sub}`}>out of 10</p>
                </CardContent>
              </Card>
              <Card className={`${lovedPeopleColors.border} ${lovedPeopleColors.bg}`}>
                <CardContent className="p-4 text-center">
                  <Heart className={`w-6 h-6 ${lovedPeopleColors.icon} mx-auto mb-2`} />
                  <p className={`text-xs ${lovedPeopleColors.label} font-medium`}>Avg Loved People</p>
                  <p className={`text-2xl font-bold ${lovedPeopleColors.value}`}>{avgLovedPeople}</p>
                  <p className={`text-xs ${lovedPeopleColors.sub}`}>out of 10</p>
                </CardContent>
              </Card>
              <Card className={`${fruitsColors.border} ${fruitsColors.bg}`}>
                <CardContent className="p-4 text-center">
                  <Sparkles className={`w-6 h-6 ${fruitsColors.icon} mx-auto mb-2`} />
                  <p className={`text-xs ${fruitsColors.label} font-medium`}>Avg Fruits of Spirit</p>
                  <p className={`text-2xl font-bold ${fruitsColors.value}`}>{avgFruits}</p>
                  <p className={`text-xs ${fruitsColors.sub}`}>out of 10</p>
                </CardContent>
              </Card>
              <Card className={`${glorifyingColors.border} ${glorifyingColors.bg}`}>
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className={`w-6 h-6 ${glorifyingColors.icon} mx-auto mb-2`} />
                  <p className={`text-xs ${glorifyingColors.label} font-medium`}>Avg Glorifying God</p>
                  <p className={`text-2xl font-bold ${glorifyingColors.value}`}>{avgGlorifying}</p>
                  <p className={`text-xs ${glorifyingColors.sub}`}>out of 10</p>
                </CardContent>
              </Card>
            </div>

            {/* Progress Over Time Chart */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                  Progress Over Time
                </CardTitle>
                <CardDescription>Your spiritual growth journey week by week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#64748b" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="#64748b" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="lovedGod" 
                        name="Loved God"
                        stroke="#f43f5e" 
                        fill="#fecdd3"
                        strokeWidth={2}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="lovedPeople" 
                        name="Loved People"
                        stroke="#3b82f6" 
                        fill="#bfdbfe"
                        strokeWidth={2}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="fruitsAvg" 
                        name="Fruits Avg"
                        stroke="#f59e0b" 
                        fill="#fde68a"
                        strokeWidth={2}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="glorifyingAvg" 
                        name="Glorifying Avg"
                        stroke="#10b981" 
                        fill="#a7f3d0"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Fruits of the Spirit Line Chart */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Fruits of the Spirit Over Time
                </CardTitle>
                <CardDescription>Track each fruit of the spirit week by week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#64748b" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} stroke="#64748b" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="love" name="Love" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="joy" name="Joy" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="peace" name="Peace" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="patience" name="Patience" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="kindness" name="Kindness" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="goodness" name="Goodness" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="faithfulness" name="Faithfulness" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="gentleness" name="Gentleness" stroke="#84cc16" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="selfControl" name="Self-Control" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Glorifying God Line Chart */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Glorifying God Over Time
                </CardTitle>
                <CardDescription>Track each spiritual discipline week by week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#64748b" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} stroke="#64748b" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="useOfWords" name="Use of Words" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="thoughtLife" name="Thought Life" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="useOfTime" name="Use of Time" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="useOfMoney" name="Use of Money" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="managementOfTemptations" name="Management of Temptations" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Overall Score Trend */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Overall Score Trend
                </CardTitle>
                <CardDescription>Combined average of all metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#64748b" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="#64748b" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="overall" 
                        name="Overall Score"
                        stroke="#10b981" 
                        strokeWidth={3}
                        dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Check-In Log */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-slate-600" />
                  Check-In Log
                </CardTitle>
                <CardDescription>Detailed history of all your check-ins</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {checkins.map((checkin, index) => {
                      const isExpanded = expandedCheckin === checkin.checkin_id;
                      const prevCheckin = checkins[index + 1];
                      
                      const currentOverall = (
                        (checkin.loved_well?.loved_god || 0) +
                        (checkin.loved_well?.loved_people || 0) +
                        (Object.values(checkin.fruits_of_spirit || {}).reduce((a, b) => a + b, 0) / 9) +
                        (Object.values(checkin.glorifying_god || {}).reduce((a, b) => a + b, 0) / 5)
                      ) / 4;
                      
                      const prevOverall = prevCheckin ? (
                        (prevCheckin.loved_well?.loved_god || 0) +
                        (prevCheckin.loved_well?.loved_people || 0) +
                        (Object.values(prevCheckin.fruits_of_spirit || {}).reduce((a, b) => a + b, 0) / 9) +
                        (Object.values(prevCheckin.glorifying_god || {}).reduce((a, b) => a + b, 0) / 5)
                      ) / 4 : null;
                      
                      const trend = calculateTrend(currentOverall, prevOverall);
                      
                      return (
                        <Card 
                          key={checkin.checkin_id}
                          className={`border-slate-100 cursor-pointer transition-all hover:border-slate-200 ${isExpanded ? 'ring-2 ring-amber-200' : ''}`}
                          onClick={() => setExpandedCheckin(isExpanded ? null : checkin.checkin_id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                  <Calendar className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    Week of {format(new Date(checkin.week_start), 'MMMM d, yyyy')}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    Submitted {format(new Date(checkin.created_at), 'MMM d, yyyy h:mm a')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                  {getTrendIcon(trend)}
                                  <span className="font-semibold text-slate-700">
                                    {currentOverall.toFixed(1)}
                                  </span>
                                </div>
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 text-slate-400" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                            
                            {isExpanded && (
                              <div className="mt-4 pt-4 border-t border-slate-100">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                  <div className="bg-rose-50 rounded-lg p-3 text-center">
                                    <p className="text-xs text-rose-600">Loved God</p>
                                    <p className="text-xl font-bold text-rose-700">
                                      {checkin.loved_well?.loved_god}/10
                                    </p>
                                  </div>
                                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <p className="text-xs text-blue-600">Loved People</p>
                                    <p className="text-xl font-bold text-blue-700">
                                      {checkin.loved_well?.loved_people}/10
                                    </p>
                                  </div>
                                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                                    <p className="text-xs text-amber-600">Fruits Avg</p>
                                    <p className="text-xl font-bold text-amber-700">
                                      {(Object.values(checkin.fruits_of_spirit || {}).reduce((a, b) => a + b, 0) / 9).toFixed(1)}/10
                                    </p>
                                  </div>
                                  <div className="bg-green-50 rounded-lg p-3 text-center">
                                    <p className="text-xs text-green-600">Glorifying Avg</p>
                                    <p className="text-xl font-bold text-green-700">
                                      {(Object.values(checkin.glorifying_god || {}).reduce((a, b) => a + b, 0) / 5).toFixed(1)}/10
                                    </p>
                                  </div>
                                </div>
                                
                                {checkin.reflection?.highlights && (
                                  <div className="bg-slate-50 rounded-lg p-3 mb-2">
                                    <p className="text-xs font-medium text-slate-500 mb-1">Highlights</p>
                                    <p className="text-sm text-slate-700">{checkin.reflection.highlights}</p>
                                  </div>
                                )}
                                
                                {checkin.reflection?.struggles && (
                                  <div className="bg-slate-50 rounded-lg p-3 mb-2">
                                    <p className="text-xs font-medium text-slate-500 mb-1">Struggles</p>
                                    <p className="text-sm text-slate-700">{checkin.reflection.struggles}</p>
                                  </div>
                                )}
                                
                                {checkin.reflection?.prayer_requests && (
                                  <div className="bg-slate-50 rounded-lg p-3">
                                    <p className="text-xs font-medium text-slate-500 mb-1">Prayer Requests</p>
                                    <p className="text-sm text-slate-700">{checkin.reflection.prayer_requests}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
